import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { PlainModal } from '@client/new/components/Modal/Modal';

type FreeTrialModalProps = {
  open: boolean;
  toggle: () => void;
  onSubmit: () => Promise<void>;
  isTrialEnabled: boolean;
  isSubmitting: boolean;
};

export const getModalConfig = (isTrialEnabled: boolean) => {
  if (isTrialEnabled) {
    return {
      title: 'Are you sure?',
      description:
        'Your prospective customers will no longer have access to a 7-day free trial. Make sure you consider the following:',
      list: [
        'Review your trial conversion rates and the potential impact on user acquisition and subscriber numbers.',
        'Risk of being perceived as less user-friendly than competitors.',
        'Possible negative effects on brand, customer satisfaction, and retention.',
        'Review your pricing strategy; competitive pricing may reduce the need for a free trial.',
        'For relatively high pricing, a free trial can help users justify the cost.',
      ],
      submitLabel: 'Disable 7-Day Free Trial',
    };
  }
  return {
    title: 'Ready to enable a 7-Day Free Trial?',
    description:
      'Free trials let users try your product, reducing purchase uncertainty and cancellations. You can track their impact on conversions and cancellations and turn off this feature anytime.',
    submitLabel: 'Enable 7-Day Free Trial',
  };
};

export const FreeTrialModal = ({
  isTrialEnabled,
  open,
  toggle,
  onSubmit,
  isSubmitting,
}: FreeTrialModalProps) => {
  const { title, description, list, submitLabel } =
    getModalConfig(isTrialEnabled);

  return (
    <PlainModal
      isOpen={open}
      onClose={toggle}
      height={'auto'}
      sx={{
        padding: '32px',
        maxWidth: '600px',
        maxHeight: 'fit-content',
      }}
      disableBackdropClick={true}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          gap: 16,
        }}
        className="flex flex-col justify-between gap-6 text-center md:text-left"
      >
        <Typography variant="h4">{title}</Typography>
        <div>
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
            {description}
          </Typography>
          {list ? (
            <ul className="list-disc list-outside m-auto pl-5 md:pl-10 pt-5">
              {list.map((item) => (
                <li key={item}>
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                    {item}
                  </Typography>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <div className="flex flex-col-reverse justify-end gap-4 md:flex-row">
          <Button variant="text" onClick={toggle}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onSubmit}
            className="w-full md:w-[187px]"
          >
            {isSubmitting ? (
              <CircularProgress className="h-[20px] w-[20px]" />
            ) : (
              submitLabel
            )}
          </Button>
        </div>
      </Box>
    </PlainModal>
  );
};
